import { PersistenceLevel, Store } from '@/core/flux.service';
import type { AnyProperty } from '@/utilities.types';
import type { InstanceConfig, TableColumns, TableConfig } from '@/vantage/types/TableConfig.types';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { getTableColumns } from '@/vantage/vantageWorkbook.utilities';
import {
  findConditionIdColumnNames,
  findConditions,
} from '@/vantage/components/evidence_table/helpers/conditionFinders';
import { addCharacteristicsForPurviewFields } from '@/vantage/queries';
import { VantageNames } from '@/vantage/constants';
import { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import { StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';

export class VantageWorkbookStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  static readonly storeName = 'sqVantageWorkbook';

  initialize(): void {
    this.state = this.immutable({
      workbookConfig: undefined,
      contextLastModified: 0,
      selectedNodes: [],
      activeExtensions: [],
      evidenceTableDefinition: undefined,
      tableColumns: this.monkey(
        ['evidenceTableDefinition'],
        ['workbookConfig'],
        (evidenceTableDefinition: TableDefinitionOutputV1 | undefined, workbookConfig: InstanceConfig | undefined) => {
          if (!evidenceTableDefinition || !workbookConfig) {
            return;
          }

          return getTableColumns(evidenceTableDefinition?.columnDefinitions, workbookConfig?.extensions);
        },
      ),
      conditionsList: this.monkey(['tableColumns'], (tableColumns: TableColumns | undefined) =>
        !tableColumns ? undefined : findConditions(tableColumns, findConditionIdColumnNames(tableColumns)),
      ),
      conditionMonitorJobPercentComplete: undefined,
      stoppedConditionMonitorState: undefined,
    });
  }

  get conditionMonitorJobPercentComplete(): number | undefined {
    return this.state.get('conditionMonitorJobPercentComplete');
  }

  get selectedNodes(): SimplifiedNode[] {
    return this.state.get('selectedNodes');
  }

  get activeExtensions(): string[] {
    return this.state.get('activeExtensions');
  }

  get workbookConfig(): InstanceConfig | undefined {
    return this.state.get('workbookConfig');
  }

  get contextLastModified(): number {
    return this.state.get('contextLastModified');
  }

  get extensions(): ExtensionSchema[] {
    return (
      this.workbookConfig?.extensions?.map((ext) =>
        ext.name === VantageNames.Extensions.Notes
          ? addCharacteristicsForPurviewFields(ext, this.workbookConfig?.tableConfig?.purviewFields ?? [])
          : ext,
      ) ?? []
    );
  }

  get tableConfig(): TableConfig | undefined {
    return this.workbookConfig?.tableConfig;
  }

  get tableIds(): AnyProperty<string> | undefined {
    return this.workbookConfig?.tableIds;
  }

  get workbookId(): string | undefined {
    return this.workbookConfig?.workbookId;
  }

  /**
   * Includes extinct events by default until the workbook config is loaded and we know it's safe to query on the "Last
   * Capsule State" column, which for some workbooks not exist.
   */
  get includeExtinct(): boolean {
    return this.workbookConfig?.includeExtinct ?? true;
  }

  get isManualEventsEnabled(): boolean {
    return this.workbookConfig?.isManualEventsEnabled ?? false;
  }

  get evidenceTableDefinition(): TableDefinitionOutputV1 | undefined {
    return this.state.get('evidenceTableDefinition');
  }

  get tableColumns(): TableColumns | undefined {
    return this.state.get('tableColumns');
  }

  get conditionsList(): string[] | undefined {
    return this.state.get('conditionsList');
  }

  /**
   * Indicates whether the condition monitor is disabled/archived
   */
  get stoppedConditionMonitorState(): StoppedConditionMonitorState | undefined {
    return this.state.get('stoppedConditionMonitorState');
  }

  protected readonly handlers = {
    WORKBOOK_SET: (payload: any) => {
      if (payload) {
        this.state.set('workbookConfig', payload.workbookConfig);
        this.state.set('evidenceTableDefinition', payload.evidenceTableDefinition);
      }
    },
    SET_CONTEXT_LAST_MODIFIED: (payload: { contextLastModified: number }) =>
      this.state.set('contextLastModified', payload.contextLastModified),
    SET_EXTENSIONS: (extensions: ExtensionSchema[]) =>
      this.state.set('workbookConfig', { ...this.workbookConfig, extensions }),
    SET_ACTIVE_EXTENSIONS: (payload: { activeExtensions: string[] }) =>
      this.state.set('activeExtensions', payload.activeExtensions),
    SET_SELECTED_NODES: (payload: { selectedNodes: SimplifiedNode[] }) =>
      this.state.set('selectedNodes', payload.selectedNodes),
    SET_EVIDENCE_TABLE_DEFINITION: (payload: { evidenceTableDefinition: TableDefinitionOutputV1 }) =>
      this.state.set('evidenceTableDefinition', payload.evidenceTableDefinition),
    SET_TABLE_COLUMNS: (payload: { tableColumns: TableColumns }) =>
      this.state.set('tableColumns', payload.tableColumns),
    SET_CONDITION_MONITOR_JOB_PERCENT_COMPLETE: (payload: { percentComplete: number | undefined }) =>
      this.state.set('conditionMonitorJobPercentComplete', payload.percentComplete),
    SET_INCLUDE_EXTINCT: (payload: { includeExtinct: boolean }) =>
      this.state.set('workbookConfig', {
        ...this.workbookConfig,
        includeExtinct: payload.includeExtinct,
      }),
    SET_IS_MANUAL_EVENTS_ENABLED: (payload: { isManualEventsEnabled: boolean }) =>
      this.state.set('workbookConfig', {
        ...this.workbookConfig,
        isManualEventsEnabled: payload.isManualEventsEnabled,
      }),
    SET_CONDITION_MONITOR_STOPPED_STATE: (payload: {
      stoppedConditionMonitorState: StoppedConditionMonitorState | undefined;
    }) => this.state.set('stoppedConditionMonitorState', payload.stoppedConditionMonitorState),
  };
}
